import type { SignatureTemplate } from "@/shared/types";
import { info, warn, debug } from "@/shared/services/logger";

const SOURCE = "sig-cache";

/**
 * Recupera templates do roaming settings ou localStorage. Retorna array vazio
 * se nenhum dos dois tiver dados (ex: usuário nunca abriu o taskpane).
 * Prioriza o roaming settings para garantir acesso compartilhado entre runtimes do add-in.
 *
 * @returns Array de templates de assinatura.
 *
 * @example
 * ```typescript
 * const templates = getCachedTemplates();
 * ```
 */
export function getCachedTemplates(): SignatureTemplate[] {
  try {
    // Prioriza roaming settings (compartilhado entre runtimes do add-in)
    if (typeof Office !== "undefined" && Office.context?.roamingSettings) {
      const roamingRaw = Office.context.roamingSettings.get("helpitech_tpl_cache");
      if (roamingRaw) {
        const rawLength = typeof roamingRaw === "string" ? roamingRaw.length : JSON.stringify(roamingRaw).length;
        const parsed: SignatureTemplate[] = typeof roamingRaw === "string"
          ? JSON.parse(roamingRaw)
          : roamingRaw as SignatureTemplate[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          info(SOURCE, `[TEL-DEBUG] Templates carregados do roamingSettings: ${parsed.length} (ids: ${parsed.map((t: SignatureTemplate) => t.id).join(",")}), raw=${rawLength} chars.`);
          for (const t of parsed) {
            const hasPhone1 = t.html.includes("office_phone_1");
            const hasPhone2 = t.html.includes("office_phone_2");
            debug(SOURCE, `[TEL-DEBUG] Template id=${t.id} html=${t.html.length} chars, placeholder office_phone_1=${hasPhone1}, office_phone_2=${hasPhone2}.`);
          }
          return parsed;
        }
      }
      debug(SOURCE, "[SOURCE] RoamingSettings sem templates — tentando localStorage.");
    } else {
      debug(SOURCE, "[SOURCE] Office.context.roamingSettings indisponível — tentando localStorage.");
    }
    // Fallback para localStorage
    const raw = localStorage.getItem("helpitech_templates_cache");
    if (!raw) {
      warn(SOURCE, "[SOURCE] localStorage vazio — nenhum template disponível.");
      return [];
    }
    const parsed = JSON.parse(raw) as SignatureTemplate[];
    if (parsed.length === 0) {
      warn(SOURCE, "[SOURCE] localStorage com array vazio — nenhum template disponível.");
      return [];
    }
    info(SOURCE, `[SOURCE] Templates carregados do localStorage: ${parsed.length} (ids: ${parsed.map((t: SignatureTemplate) => t.id).join(",")})`);
    return parsed;
  } catch (err: any) {
    warn(SOURCE, `[SOURCE] Erro ao ler cache: ${err?.message || err} — nenhum template disponível.`);
    return [];
  }
}

/**
 * Extrai o ID numérico de um nome de template no formato `template_<id>`.
 *
 * @param templateName - Identificador no formato `template_<id>`.
 * @returns O ID numérico ou `0` se o formato for inválido.
 *
 * @example
 * ```typescript
 * extractTemplateId("template_3"); // 3
 * extractTemplateId("invalido");   // 0
 * ```
 */
export function extractTemplateId(templateName: string): number {
  const match = templateName.match(/^template_(\d+)$/);
  return match ? parseInt(match[1], 10) : 0;
}

/**
 * Obtém o nome do template com base no tipo de composição a partir do roaming settings.
 *
 * @param composeType - Tipo de composição (`new_mail`, `reply` ou `forward`).
 * @returns Nome do template no formato `template_<id>` ou `"none"`.
 *
 * @example
 * ```typescript
 * const name = getTemplateName("reply"); // "template_1" ou "none"
 * ```
 */
export function getTemplateName(composeType: string): string {
  const slot = composeType === "reply" || composeType === "replyAll"
    ? "reply"
    : composeType === "forward"
      ? "forward"
      : "new_mail";

  let template = Office.context.roamingSettings.get(slot) as string;
  if (!template) {
    try {
      template = localStorage.getItem(slot) || "";
    } catch {
      template = "";
    }
  }

  info(SOURCE, `[RESOLVE] getTemplateName: composeType=${composeType}, roamingValue=${template || "(vazio)"}`);

  const cached = getCachedTemplates();

  const mandatory = cached.find((t) => t.mandatory);
  if (mandatory) {
    const forced = `template_${mandatory.id}`;
    if (forced !== template) {
      info(SOURCE, `[RESOLVE] Template obrigatório ${forced} sobrepõe o slot ${template || "(vazio)"}.`);
    }
    return forced;
  }

  const isConfigured = template && template !== "none";
  const isResolvable = isConfigured && cached.some((t) => t.id === extractTemplateId(template));

  if (isConfigured && !isResolvable && cached.length > 0) {
    warn(SOURCE, `[RESOLVE] Slot ${slot}=${template} não corresponde a nenhum template do cache — usando fallback.`);
  }

  if (!isResolvable) {
    if (cached.length > 0) {
      const candidate = cached.find((t) => t.is_default) || cached[0];
      const fallback = `template_${candidate.id}`;
      info(SOURCE, `[RESOLVE] Fallback para ${composeType}: ${fallback}${candidate.is_default ? " (is_default)" : " (primeiro do cache)"}`);
      return fallback;
    }
    info(SOURCE, `[RESOLVE] Nenhum template disponível para ${composeType} → retornando "none".`);
    return "none";
  }

  info(SOURCE, `[RESOLVE] Template ${template} selecionado para composeType=${composeType}.`);
  return template;
}
